import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { buildApiUrl } from "../config/api";

const CHECKOUT_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";

const SERVICE_OPTIONS = [
  {
    key: "domain_renewal",
    label: "Domain Renewal",
    defaultAmountInRupees: 700,
    category: "domain",
  },
  {
    key: "new_domain",
    label: "New Domain Registration",
    defaultAmountInRupees: 1200,
    category: "domain",
  },
  {
    key: "starter_email",
    label: "Starter Email",
    defaultAmountInRupees: 149,
    category: "email",
  },
  {
    key: "professional_email",
    label: "Professional Email",
    defaultAmountInRupees: 349,
    category: "email",
  },
  {
    key: "enterprise_email",
    label: "Enterprise Email",
    defaultAmountInRupees: 699,
    category: "email",
  },
  {
    key: "managed_ssl",
    label: "Managed SSL Setup",
    defaultAmountInRupees: 999,
    category: "ssl",
  },
  {
    key: "external_ssl",
    label: "External Domain SSL Setup",
    defaultAmountInRupees: 1499,
    category: "ssl",
  },
  {
    key: "wildcard_ssl",
    label: "Wildcard SSL Planning",
    defaultAmountInRupees: 2499,
    category: "ssl",
  },
  {
    key: "portfolio_hosting",
    label: "Portfolio Hosting",
    defaultAmountInRupees: 149,
    category: "hosting",
  },
  {
    key: "ui_hosting",
    label: "UI Website Hosting",
    defaultAmountInRupees: 399,
    category: "hosting",
  },
  {
    key: "full_stack_hosting",
    label: "Full Stack Hosting",
    defaultAmountInRupees: 999,
    category: "hosting",
  },
  {
    key: "website_design",
    label: "Website Design",
    defaultAmountInRupees: 15000,
    category: "website",
  },
  {
    key: "seo_service",
    label: "SEO Service",
    defaultAmountInRupees: 8000,
    category: "seo",
  },
  {
    key: "seo_audit_fixes",
    label: "SEO Audit & Fixes",
    defaultAmountInRupees: 4999,
    category: "seo",
  },
  {
    key: "local_seo_growth",
    label: "Local SEO Growth",
    defaultAmountInRupees: 8999,
    category: "seo",
  },
  {
    key: "content_seo_retainer",
    label: "Content SEO Retainer",
    defaultAmountInRupees: 14999,
    category: "seo",
  },
  {
    key: "ppc_launch_setup",
    label: "PPC Launch Setup",
    defaultAmountInRupees: 6999,
    category: "ppc",
  },
  {
    key: "search_ads_management",
    label: "Search Ads Management",
    defaultAmountInRupees: 9999,
    category: "ppc",
  },
  {
    key: "growth_ads_management",
    label: "Growth Ads Management",
    defaultAmountInRupees: 17999,
    category: "ppc",
  },
  {
    key: "photoshop",
    label: "Photoshop",
    defaultAmountInRupees: 2500,
    category: "design",
  },
  {
    key: "website_full_service",
    label: "Website Launch Complete",
    defaultAmountInRupees: 22500,
    category: "website",
  },
  {
    key: "website_own_domain",
    label: "Website Build & Deploy",
    defaultAmountInRupees: 16500,
    category: "website",
  },
];

const COPY = {
  title: "Razorpay Payment",
  subtitle: "Complete payment securely. After verification, your service order is created automatically.",
  serviceLabel: "Selected Service",
  amountLabel: "Amount (INR)",
  amountHint: "Subtotal is locked from the selected service. GST is added before payment.",
  amountValidation: "Please enter a valid amount of at least 1 INR.",
  proceed: "Pay With Razorpay",
  processing: "Opening Checkout...",
  back: "Back",
  paymentSuccess: "Payment verified successfully.",
  paymentCancelled: "Payment window closed.",
  genericError: "Unable to start payment right now. Please try again.",
  verificationFailed: "Payment completed but verification failed. Please contact support.",
  authError: "Please log in again to continue with payment.",
  customerValidation: "Enter your name, email, and phone number before payment.",
};

const parseTokenPayload = (token) => {
  if (!token) {
    return null;
  }

  try {
    const [, payload] = token.split(".");

    if (!payload) {
      return null;
    }

    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padding = "=".repeat((4 - (normalizedPayload.length % 4)) % 4);
    const decodedPayload = atob(`${normalizedPayload}${padding}`);
    return JSON.parse(decodedPayload);
  } catch (error) {
    return null;
  }
};

const loadRazorpayCheckout = () =>
  new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Browser environment unavailable."));
      return;
    }

    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existingScript = document.querySelector(`script[src="${CHECKOUT_SCRIPT_URL}"]`);

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(true), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Razorpay SDK failed to load.")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.src = CHECKOUT_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error("Razorpay SDK failed to load."));
    document.body.appendChild(script);
  });

const getErrorMessage = (error, fallbackMessage) =>
  String(error?.response?.data?.error || error?.message || fallbackMessage);

const getServiceOption = (serviceKey) =>
  SERVICE_OPTIONS.find((service) => service.key === serviceKey) || SERVICE_OPTIONS[1];

const getEmailAccounts = (value) => {
  if (Number.isFinite(Number(value))) {
    return Math.max(1, Number(value));
  }

  const match = String(value || "").match(/\d+/);
  return match ? Number(match[0]) : 1;
};

const normalizeDomain = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "");

const getDomainTld = (domain) => {
  const parts = String(domain || "").split(".").filter(Boolean);
  return parts.length > 1 ? parts[parts.length - 1] : "";
};

const getDisplayName = (details) =>
  [details.firstName, details.lastName].filter(Boolean).join(" ") ||
  details.fullName ||
  "";

const getMissingDomainRegistrationFields = (details) =>
  [
    ["firstName", "first name"],
    ["lastName", "last name"],
    ["email", "email"],
    ["phone", "phone"],
    ["addressLine1", "address"],
    ["city", "city"],
    ["state", "state"],
    ["postalCode", "postal code"],
    ["country", "country"],
  ]
    .filter(([field]) => !String(details[field] || "").trim())
    .map(([, label]) => label);

export default function RazorpayPayment() {
  const navigate = useNavigate();
  const location = useLocation();
  const checkoutData = location.state || {};
  const initialService = getServiceOption(checkoutData.serviceKey);

  const [selectedServiceKey] = useState(initialService.key);
  const [amountInput] = useState(
    String(checkoutData.amount || initialService.defaultAmountInRupees)
  );
  const [isStartingPayment, setIsStartingPayment] = useState(false);
  const [customerDetails, setCustomerDetails] = useState({
    firstName: "",
    lastName: "",
    fullName: "",
    email: "",
    phone: "",
    companyName: "",
    gstNumber: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "IN",
  });
  const [domainRegistrationConsent, setDomainRegistrationConsent] = useState(false);
  const [domainAgreements, setDomainAgreements] = useState([]);
  const [agreementsStatus, setAgreementsStatus] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const selectedService = useMemo(() => getServiceOption(selectedServiceKey), [selectedServiceKey]);
  const numericAmount = useMemo(() => Number.parseFloat(amountInput), [amountInput]);
  const gstAmount = useMemo(() => Number((numericAmount * 0.18).toFixed(2)), [numericAmount]);
  const finalAmount = useMemo(
    () => Number((numericAmount + gstAmount).toFixed(2)),
    [numericAmount, gstAmount]
  );
  const orderCategory = checkoutData.orderType || selectedService.category;
  const domainName = normalizeDomain(checkoutData.domainName || checkoutData.domain);
  const requestedEmailAddress = String(checkoutData.requestedEmailAddress || "")
    .trim()
    .toLowerCase();
  const requestedMailboxName = String(
    checkoutData.mailboxName || checkoutData.requestedMailbox || ""
  )
    .trim()
    .toLowerCase();
  const displayRequestedEmail =
    requestedEmailAddress ||
    (requestedMailboxName && domainName ? `${requestedMailboxName}@${domainName}` : "");
  const domainSource =
    selectedService.category === "domain" ? "asiduo_registered" : checkoutData.domainSource || "external";
  const requiresDomain = ["domain", "email", "ssl", "hosting"].includes(orderCategory);
  const isDomainRegistration =
    selectedService.category === "domain" && selectedService.key !== "domain_renewal";

  useEffect(() => {
    if (!isDomainRegistration || !domainName) {
      setDomainAgreements([]);
      setAgreementsStatus("");
      return;
    }

    const controller = new AbortController();
    const tld = getDomainTld(domainName);

    if (!tld) {
      return;
    }

    setAgreementsStatus("Loading domain registration agreements...");

    fetch(buildApiUrl(`/api/domains/agreements?tld=${encodeURIComponent(tld)}`), {
      signal: controller.signal,
    })
      .then(async (response) => {
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Unable to load agreements.");
        }

        setDomainAgreements(data.agreements || []);
        setAgreementsStatus("");
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          return;
        }

        setDomainAgreements([]);
        setAgreementsStatus(error.message);
      });

    return () => controller.abort();
  }, [domainName, isDomainRegistration]);

  const updateCustomerDetails = (field, value) => {
    setCustomerDetails((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const createBooking = async ({ token, razorpayOrderId, paymentId }) => {
    const headers = {
      Authorization: `Bearer ${token}`,
    };
    const normalizedCustomerDetails = {
      ...customerDetails,
      fullName: getDisplayName(customerDetails),
    };
    const commonPayload = {
      serviceKey: selectedService.key,
      serviceName: selectedService.label,
      amountPaid: finalAmount,
      paymentId,
      razorpayOrderId,
      customerDetails: normalizedCustomerDetails,
    };

    if (selectedService.category === "domain") {
      const response = await axios.post(
        buildApiUrl("/api/domain-booking"),
        {
          ...commonPayload,
          domain: checkoutData.domain,
          domainAction: selectedService.key === "domain_renewal" ? "renewal" : "registration",
          registrationPrice: checkoutData.registrationPrice || numericAmount,
          renewalPrice: checkoutData.renewalPrice || 0,
          domainRegistrationConsent: {
            accepted: domainRegistrationConsent,
            acceptedAt: new Date().toISOString(),
          },
          requestedAddOns: checkoutData.requestedAddOns || {
            dns: true,
            ssl: true,
            businessEmail: false,
            hosting: false,
          },
        },
        { headers }
      );

      return response.data;
    }

    if (selectedService.category === "email") {
      const response = await axios.post(
        buildApiUrl("/api/email-booking"),
        {
          ...commonPayload,
          planName: checkoutData.emailPlan || selectedService.label,
          provider: checkoutData.provider || "Private Email",
          domainName,
          domainSource,
          requestedMailbox: requestedMailboxName,
          requestedEmailAddress: displayRequestedEmail,
          emailAccounts: getEmailAccounts(checkoutData.emailAccounts),
          storage: checkoutData.storage || "5GB",
        },
        { headers }
      );

      return response.data;
    }

    const response = await axios.post(
      buildApiUrl("/api/service-booking"),
      {
        ...commonPayload,
        serviceCategory: selectedService.category,
        domainName,
        domainSource: domainName ? domainSource : "not_applicable",
        engagementType: checkoutData.engagementType || "not_applicable",
        feeBreakdown: checkoutData.feeBreakdown || null,
        milestonePlan: checkoutData.milestonePlan || null,
        setupPreference: checkoutData.setupPreference || selectedService.label,
        notes: checkoutData.notes || "",
      },
      { headers }
    );

    return response.data;
  };

  const handleProceed = async () => {
    setStatusMessage("");
    setErrorMessage("");

    const token = localStorage.getItem("token");

    if (!token) {
      setErrorMessage(COPY.authError);
      return;
    }

    if (!Number.isFinite(numericAmount) || numericAmount < 1) {
      setErrorMessage(COPY.amountValidation);
      return;
    }

    if (isDomainRegistration) {
      const missingRegistrationFields = getMissingDomainRegistrationFields(customerDetails);

      if (missingRegistrationFields.length > 0) {
        setErrorMessage(`Enter domain registrant ${missingRegistrationFields.join(", ")} before payment.`);
        return;
      }

      if (!domainRegistrationConsent) {
        setErrorMessage("Accept the domain registration agreements before payment.");
        return;
      }
    } else if (!getDisplayName(customerDetails).trim() || !customerDetails.email.trim() || !customerDetails.phone.trim()) {
      setErrorMessage(COPY.customerValidation);
      return;
    }

    if (requiresDomain && !domainName) {
      setErrorMessage("A domain name is required for this service.");
      return;
    }

    const safeAmountInRupees = Math.round(finalAmount * 100) / 100;
    const tokenPayload = parseTokenPayload(token);
    const customerEmail = customerDetails.email || tokenPayload?.email || "";
    const customerName = getDisplayName(customerDetails);

    setIsStartingPayment(true);

    try {
      await loadRazorpayCheckout();

      const orderResponse = await axios.post(
        buildApiUrl("/api/payments/razorpay/order"),
        {
          amountInRupees: safeAmountInRupees,
          serviceKey: selectedService.key,
          serviceName: selectedService.label,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const keyId = orderResponse.data?.keyId;
      const order = orderResponse.data?.order;

      if (!keyId || !order?.id) {
        throw new Error(COPY.genericError);
      }

      const options = {
        key: keyId,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "Asiduo Solutions",
        description: `${selectedService.label} - ${safeAmountInRupees.toFixed(2)} INR`,
        order_id: order.id,
        prefill: {
          name: customerName,
          email: customerEmail,
          contact: customerDetails.phone,
        },
        theme: {
          color: "#38bdf8",
        },
        handler: async (response) => {
          try {
            const verifyResponse = await axios.post(
              buildApiUrl("/api/payments/razorpay/verify"),
              {
                orderId: order.id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            if (!verifyResponse.data?.verified) {
              throw new Error(COPY.verificationFailed);
            }

            const booking = await createBooking({
              token,
              razorpayOrderId: order.id,
              paymentId: response.razorpay_payment_id,
            });

            const paymentStatus = verifyResponse.data?.status
              ? ` (${verifyResponse.data.status})`
              : "";
            setStatusMessage(`${COPY.paymentSuccess}${paymentStatus}`);
            setErrorMessage("");

            navigate("/payment-success", {
              state: {
                serviceName: selectedService.label,
                serviceKey: selectedService.key,
                orderType: booking.orderType || orderCategory,
                bookingId: booking.orderId,
                domain: domainName,
                domainSource,
                requestedEmailAddress: booking.requestedEmailAddress || displayRequestedEmail,
                paymentId: response.razorpay_payment_id,
                amount: finalAmount,
                setupPlan: booking.setupPlan,
                dnsRecordsNeeded: booking.dnsRecordsNeeded,
                mailboxCredentials: booking.mailboxCredentials,
                credentialsDelivery: booking.credentialsDelivery,
                domainRegistration: booking.domainRegistration,
                warning: booking.warning,
              },
            });
          } catch (error) {
            setStatusMessage("");
            setErrorMessage(getErrorMessage(error, COPY.verificationFailed));
          }
        },
        modal: {
          ondismiss: () => {
            setStatusMessage(COPY.paymentCancelled);
          },
        },
      };

      const razorpayInstance = new window.Razorpay(options);

      razorpayInstance.on("payment.failed", (response) => {
        const failureReason = response?.error?.description || COPY.genericError;
        setStatusMessage("");
        setErrorMessage(failureReason);
      });

      razorpayInstance.open();
    } catch (error) {
      setStatusMessage("");
      setErrorMessage(getErrorMessage(error, COPY.genericError));
    } finally {
      setIsStartingPayment(false);
    }
  };

  return (
    <main className="safe-mobile-padding flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-950 via-sky-950 to-slate-900 px-4 py-24 text-white">
      <section className="w-full max-w-2xl rounded-3xl border border-sky-200/30 bg-slate-900/70 p-8 text-center shadow-2xl backdrop-blur-sm sm:p-10">
        <h1 className="text-3xl font-black text-sky-300 sm:text-4xl">
          {COPY.title}
        </h1>
        <p className="mt-4 text-sm text-slate-200 sm:text-base">
          {COPY.subtitle}
        </p>

        <div className="mt-6 rounded-2xl border border-sky-100/20 bg-slate-950/55 p-5 text-left">
          <p className="text-sm text-slate-300">{COPY.serviceLabel}</p>
          <h2 className="mt-2 text-2xl font-black text-sky-300">
            {selectedService.label}
          </h2>

          {domainName ? (
            <p className="mt-3 text-sm text-slate-300">
              Domain: <span className="font-semibold text-white">{domainName}</span>
            </p>
          ) : null}

          {selectedService.category === "email" ? (
            <>
              {displayRequestedEmail ? (
                <p className="mt-2 text-sm text-slate-300">
                  Requested Email:{" "}
                  <span className="font-semibold text-white">{displayRequestedEmail}</span>
                </p>
              ) : null}

              <p className="mt-2 text-sm text-slate-300">
                Provider: {checkoutData.provider || "Private Email"} | Source:{" "}
                {domainSource === "asiduo_registered" ? "Managed by Asiduo" : "External domain"}
              </p>
            </>
          ) : null}
        </div>

        <div className="mt-6 rounded-2xl border border-sky-100/20 bg-slate-950/55 p-4 text-left">
          <label htmlFor="razorpay-amount" className="block text-sm font-semibold text-sky-200">
            {COPY.amountLabel}
          </label>
          <input
            id="razorpay-amount"
            type="number"
            min="1"
            step="0.01"
            value={amountInput}
            readOnly
            className="mt-2 w-full cursor-not-allowed rounded-xl border border-sky-100/40 bg-slate-950/80 px-4 py-2.5 text-white opacity-85 outline-none"
          />
          <p className="mt-2 text-xs text-slate-300">{COPY.amountHint}</p>

          <div className="mt-4 rounded-xl border border-sky-100/20 bg-slate-900/60 p-4">
            <div className="flex items-center justify-between text-sm text-slate-300">
              <span>Subtotal</span>
              <span>Rs. {numericAmount}</span>
            </div>

            <div className="mt-2 flex items-center justify-between text-sm text-slate-300">
              <span>GST (18%)</span>
              <span>Rs. {gstAmount}</span>
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-sky-100/10 pt-4 text-lg font-bold text-sky-300">
              <span>Total Payable</span>
              <span>Rs. {finalAmount}</span>
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-sky-100/20 bg-slate-950/55 p-4 text-left">
          <h3 className="text-lg font-bold text-sky-300">
            {isDomainRegistration ? "Domain Registrant Information" : "Customer Information"}
          </h3>

          {isDomainRegistration ? (
            <p className="mt-2 text-sm text-slate-300">
              These details are sent to GoDaddy for ICANN domain registration.
            </p>
          ) : null}

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {isDomainRegistration ? (
              <>
                <input
                  type="text"
                  placeholder="First Name"
                  value={customerDetails.firstName}
                  onChange={(e) => updateCustomerDetails("firstName", e.target.value)}
                  className="w-full rounded-xl border border-sky-100/30 bg-slate-950/80 px-4 py-3 text-white outline-none"
                />

                <input
                  type="text"
                  placeholder="Last Name"
                  value={customerDetails.lastName}
                  onChange={(e) => updateCustomerDetails("lastName", e.target.value)}
                  className="w-full rounded-xl border border-sky-100/30 bg-slate-950/80 px-4 py-3 text-white outline-none"
                />
              </>
            ) : (
              <input
                type="text"
                placeholder="Full Name"
                value={customerDetails.fullName}
                onChange={(e) => updateCustomerDetails("fullName", e.target.value)}
                className="w-full rounded-xl border border-sky-100/30 bg-slate-950/80 px-4 py-3 text-white outline-none sm:col-span-2"
              />
            )}

            <input
              type="email"
              placeholder="Email"
              value={customerDetails.email}
              onChange={(e) => updateCustomerDetails("email", e.target.value)}
              className="w-full rounded-xl border border-sky-100/30 bg-slate-950/80 px-4 py-3 text-white outline-none"
            />

            <input
              type="text"
              placeholder="Phone Number"
              value={customerDetails.phone}
              onChange={(e) => updateCustomerDetails("phone", e.target.value)}
              className="w-full rounded-xl border border-sky-100/30 bg-slate-950/80 px-4 py-3 text-white outline-none"
            />

            <input
              type="text"
              placeholder="Company Name (Optional)"
              value={customerDetails.companyName}
              onChange={(e) => updateCustomerDetails("companyName", e.target.value)}
              className="w-full rounded-xl border border-sky-100/30 bg-slate-950/80 px-4 py-3 text-white outline-none"
            />

            <input
              type="text"
              placeholder="GST Number (Optional)"
              value={customerDetails.gstNumber}
              onChange={(e) => updateCustomerDetails("gstNumber", e.target.value)}
              className="w-full rounded-xl border border-sky-100/30 bg-slate-950/80 px-4 py-3 text-white outline-none"
            />

            <input
              type="text"
              placeholder={isDomainRegistration ? "Address Line 1" : "Address (Optional)"}
              value={customerDetails.addressLine1}
              onChange={(e) => updateCustomerDetails("addressLine1", e.target.value)}
              className="w-full rounded-xl border border-sky-100/30 bg-slate-950/80 px-4 py-3 text-white outline-none sm:col-span-2"
            />

            {isDomainRegistration ? (
              <>
                <input
                  type="text"
                  placeholder="Address Line 2 (Optional)"
                  value={customerDetails.addressLine2}
                  onChange={(e) => updateCustomerDetails("addressLine2", e.target.value)}
                  className="w-full rounded-xl border border-sky-100/30 bg-slate-950/80 px-4 py-3 text-white outline-none sm:col-span-2"
                />

                <input
                  type="text"
                  placeholder="City"
                  value={customerDetails.city}
                  onChange={(e) => updateCustomerDetails("city", e.target.value)}
                  className="w-full rounded-xl border border-sky-100/30 bg-slate-950/80 px-4 py-3 text-white outline-none"
                />

                <input
                  type="text"
                  placeholder="State"
                  value={customerDetails.state}
                  onChange={(e) => updateCustomerDetails("state", e.target.value)}
                  className="w-full rounded-xl border border-sky-100/30 bg-slate-950/80 px-4 py-3 text-white outline-none"
                />

                <input
                  type="text"
                  placeholder="Postal Code"
                  value={customerDetails.postalCode}
                  onChange={(e) => updateCustomerDetails("postalCode", e.target.value)}
                  className="w-full rounded-xl border border-sky-100/30 bg-slate-950/80 px-4 py-3 text-white outline-none"
                />

                <input
                  type="text"
                  placeholder="Country Code"
                  value={customerDetails.country}
                  onChange={(e) => updateCustomerDetails("country", e.target.value.toUpperCase())}
                  className="w-full rounded-xl border border-sky-100/30 bg-slate-950/80 px-4 py-3 text-white outline-none"
                />
              </>
            ) : null}
          </div>

          {isDomainRegistration ? (
            <div className="mt-5 rounded-xl border border-sky-100/20 bg-slate-900/60 p-4">
              <p className="text-sm font-bold text-sky-200">Domain Agreements</p>

              {domainAgreements.length ? (
                <div className="mt-3 space-y-2 text-sm text-slate-300">
                  {domainAgreements.map((agreement) => (
                    <p key={agreement.agreementKey}>
                      {agreement.url ? (
                        <a
                          href={agreement.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sky-300 underline"
                        >
                          {agreement.title || agreement.agreementKey}
                        </a>
                      ) : (
                        agreement.title || agreement.agreementKey
                      )}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="mt-3 text-sm text-slate-300">
                  {agreementsStatus || "Required registrar agreements will be attached during registration."}
                </p>
              )}

              <label className="mt-4 flex items-start gap-3 text-sm text-slate-200">
                <input
                  type="checkbox"
                  checked={domainRegistrationConsent}
                  onChange={(event) => setDomainRegistrationConsent(event.target.checked)}
                  className="mt-1 h-4 w-4"
                />
                <span>
                  I confirm these registrant details are accurate and I accept the required
                  GoDaddy domain registration agreements for this domain.
                </span>
              </label>
            </div>
          ) : null}
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={handleProceed}
            disabled={isStartingPayment}
            className="rounded-full border border-sky-200 bg-sky-500/20 px-6 py-3 text-sm font-bold tracking-wide text-white transition-all duration-300 hover:scale-105 hover:bg-sky-500/35 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:cursor-not-allowed disabled:opacity-65"
          >
            {isStartingPayment ? COPY.processing : COPY.proceed}
          </button>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-full border border-white/50 bg-white/10 px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60"
          >
            {COPY.back}
          </button>
        </div>

        {statusMessage ? <p className="mt-5 text-sm text-emerald-300">{statusMessage}</p> : null}
        {errorMessage ? <p className="mt-3 text-sm text-red-300">{errorMessage}</p> : null}
      </section>
    </main>
  );
}
